import React, { FC } from 'react';

import Head from 'next/head';
import { useRouter } from 'next/router';

import { Locale } from '@/constants/locale';
import { Metadata } from '@/types/metadata';
import { buildPageUrl, getCanonicalPageUrl, isBuyFishPageUrl } from '@/utils/url';

type Props = {
	metadata: Metadata;
};

export const HeadWithMetaData: FC<Props> = ({ metadata }) => {
	const router = useRouter();
	const locale = router.locale === Locale.En ? Locale.En : Locale.Ru;
	const routeUrl = router.asPath.split('#')[0];
	const pageUrl = buildPageUrl(routeUrl, locale);
	const { imageUrl } = metadata;
	const baseUrl = (process.env.HOST || 'https://fishplace.ru').replace(/\/+$/, '');
	const previewImageUrl = imageUrl.replace('webp', 'jpg').replace('https://fishplace.ru', baseUrl);
	const isBuyFishPage = isBuyFishPageUrl(pageUrl);
	const canonicalPageUrl = getCanonicalPageUrl(pageUrl);
	const canonicalUrl = `${baseUrl}${canonicalPageUrl}`;
	const ruUrl = `${baseUrl}${getCanonicalPageUrl(buildPageUrl(routeUrl, Locale.Ru))}`;
	const enUrl = `${baseUrl}${getCanonicalPageUrl(buildPageUrl(routeUrl, Locale.En))}`;

	const AdditionalTags = () => {
		if (!metadata.additionalTags?.length) return null;

		return metadata.additionalTags.map((tag) => {
			const key = [tag.name, ...Object.entries(tag.attributes).flat()].join('-');
			return React.createElement(tag.name, {
				...tag.attributes,
				key,
			});
		});
	};

	return (
		<Head>
			<title key="title">{metadata.title}</title>
			<meta key="description" name="description" content={metadata.description} />
			<meta key="viewport" name="viewport" content="width=device-width, initial-scale=1" />
			<link key="favicon" rel="icon" href="/favicon.ico" />
			<link key="canonical" rel="canonical" href={canonicalUrl} />
			<link key="alternate-ru" rel="alternate" hrefLang="ru" href={ruUrl} />
			<link key="alternate-en" rel="alternate" hrefLang="en" href={enUrl} />
			<link key="alternate-default" rel="alternate" hrefLang="x-default" href={ruUrl} />
			<meta key="og:title" property="og:title" content={metadata.title} />
			<meta key="og:type" property="og:type" content={isBuyFishPage ? 'product' : 'website'} />
			<meta
				key="og:locale"
				property="og:locale"
				content={locale === Locale.En ? 'en_US' : 'ru_RU'}
			/>
			<meta
				key="og:locale:alternate"
				property="og:locale:alternate"
				content={locale === Locale.En ? 'ru_RU' : 'en_US'}
			/>
			<meta key="og:description" property="og:description" content={metadata.description} />
			<meta key="og:image" property="og:image" content={previewImageUrl} />
			<meta key="og:image:secure_url" property="og:image:secure_url" content={previewImageUrl} />
			<meta key="og:image:type" property="og:image:type" content="image/jpeg" />
			<meta key="og:url" property="og:url" content={canonicalUrl} />
			<meta key="og:site_name" property="og:site_name" content="fishplace.ru" />
			<meta key="twitter:card" name="twitter:card" content="summary_large_image" />
			<meta key="twitter:title" name="twitter:title" content={metadata.title} />
			<meta key="twitter:description" name="twitter:description" content={metadata.description} />
			<meta key="twitter:image" name="twitter:image" content={previewImageUrl} />
			<meta key="twitter:url" name="twitter:url" content={canonicalUrl} />
			{AdditionalTags()}
		</Head>
	);
};
